<div class="integrations-overview">
  <div class="header">
    <div>
      <h2>{{ 'INTEGRATION.OVERVIEW.TITLE' | translate }}</h2>
      <div class="sub">{{ 'INTEGRATION.OVERVIEW.SUBTITLE' | translate }}</div>
    </div>

    <div class="header-actions">
      <button nz-button nzType="default" (click)="reloadAll()" [disabled]="loading">
        {{ 'INTEGRATION.STATUS.REFRESH' | translate }}
      </button>
    </div>
  </div>

  <div class="toolbar">
    <input
      nz-input
      [placeholder]="'INTEGRATION.OVERVIEW.SEARCH_PLACEHOLDER' | translate"
      [(ngModel)]="search"
    />

    <nz-select [(ngModel)]="filter" style="min-width: 220px;">
      <nz-option nzValue="all" [nzLabel]="'INTEGRATION.OVERVIEW.FILTER.ALL' | translate"></nz-option>
      <nz-option nzValue="connected" [nzLabel]="'INTEGRATION.OVERVIEW.FILTER.CONNECTED' | translate"></nz-option>
      <nz-option nzValue="not_connected" [nzLabel]="'INTEGRATION.OVERVIEW.FILTER.NOT_CONNECTED' | translate"></nz-option>
      <nz-option nzValue="needs_attention" [nzLabel]="'INTEGRATION.OVERVIEW.FILTER.NEEDS_ATTENTION' | translate"></nz-option>
    </nz-select>
  </div>

  <div class="sub">
    {{ 'INTEGRATION.LIMIT_SUMMARY' | translate:{ used: integrationsUsed, allowed: integrationsAllowed } }}
  </div>
  <div class="sub" *ngIf="integrationsAllowed === 0">
    {{ 'ERRORS.INTEGRATIONS_LIMIT_REACHED_CONTACT' | translate }}
  </div>

  <h3 class="section-title">{{ 'INTEGRATION.OVERVIEW.SECTION.PROVIDERS' | translate }}</h3>

  <div class="catalog">
    <nz-card
      class="catalog-card"
      [ngClass]="{ 'is-coming-soon': !isProviderAvailable(p) }"
      *ngFor="let p of providers"
      [hidden]="!providerCardVisible(p)"
    >
      <div class="card-top">
        <div class="top-row">
          <div class="brand">
            <div class="logo-box" *ngIf="p.provider === 'sftp'; else providerLogo">
              <img class="provider-logo" src="/assets/integration-providers/haussoft.svg" [alt]="'INTEGRATION.SFTP.DATA_SOURCE_NAME' | translate" />
            </div>

            <ng-template #providerLogo>
              <div class="logo-box" *ngIf="p.logoUrl; else noLogo">
                <img class="provider-logo" [src]="p.logoUrl" [alt]="p.displayName + ' logo'" />
              </div>
            </ng-template>

            <ng-template #noLogo>
              <nz-avatar nzText="{{ (p.displayName || p.provider || '?').slice(0,1).toUpperCase() }}"></nz-avatar>
            </ng-template>

            <div class="titles">
              <div class="name">{{ p.displayName }}</div>
              <small *ngIf="p.provider === 'sftp'" class="sftp-data-source">{{ 'INTEGRATION.SFTP.DATA_SOURCE_NAME' | translate }}</small>
            </div>
          </div>

          <ng-container *ngIf="getConnection(p.provider) as conn; else noConn">
            <nz-tag [nzColor]="providerStatusTag(p, conn).color">{{ providerStatusTag(p, conn).text }}</nz-tag>
          </ng-container>
          <ng-template #noConn>
            <nz-tag [nzColor]="providerStatusTag(p).color">{{ providerStatusTag(p).text }}</nz-tag>
          </ng-template>
        </div>
        <div class="desc">
          {{ getProviderDescription(p) }}
        </div>
        <div class="meta">
          <div><b>{{ 'INTEGRATION.OVERVIEW.META.DATA' | translate }}:</b> {{ capabilitiesLabel(p) }}</div>

          <ng-container *ngIf="getConnection(p.provider) as conn">
            <div *ngIf="isSftpConnection(conn); else genericLastSync">
              <b>{{ 'INTEGRATION.OVERVIEW.META.LAST_IMPORT' | translate }}:</b>
              {{ formatSftpDateTime(conn.sftpLastImportFinishedAt) }}
            </div>
            <ng-template #genericLastSync>
              <div><b>{{ 'INTEGRATION.OVERVIEW.META.LAST_SYNC' | translate }}:</b> {{ conn.lastSyncAt || '-' }}</div>
            </ng-template>
            <div><b>{{ 'INTEGRATION.OVERVIEW.META.MODULES_ENABLED' | translate }}:</b> {{ enabledModulesCount(conn) }}</div>
            <div><b>{{ 'INTEGRATION.OVERVIEW.META.SCOPE' | translate }}:</b> {{ scopeCount(conn) }}</div>
            <div><b>{{ 'INTEGRATION.OVERVIEW.META.MODE' | translate }}:</b> {{ syncMode(conn) }}</div>
          </ng-container>
        </div>
      </div>

      <div class="card-actions">
        <button
          nz-button
          nzType="primary"
          (click)="primaryCta(p)"
          [disabled]="!isProviderAvailable(p)"
        >
          {{ primaryCtaLabel(p) }}
        </button>

        <ng-container *ngIf="getConnection(p.provider) as conn">
          <button *ngIf="!isSftpConnection(conn)" nz-button nzType="default" (click)="quickTest(conn)">{{ 'INTEGRATION.STATUS.TEST' | translate }}</button>
          <button *ngIf="!isSftpConnection(conn)" nz-button nzType="default" (click)="quickSync(conn)">{{ 'INTEGRATION.OVERVIEW.SYNC' | translate }}</button>
          <button nz-button nzType="default" (click)="goToStatus(conn)">{{ 'INTEGRATION.OVERVIEW.STATUS' | translate }}</button>
          <button *ngIf="!isSftpConnection(conn) || conn.sftpLifecycleStatus !== 'disconnected'" nz-button nzType="link" (click)="disconnect(conn)">{{ 'INTEGRATION.STATUS.DISCONNECT' | translate }}</button>
        </ng-container>

        <ng-container *ngIf="!getConnection(p.provider)">
          <button nz-button nzType="default" (click)="goToSetup(p)" [disabled]="!isProviderAvailable(p)">
            {{ 'INTEGRATION.STATUS.SETUP' | translate }}
          </button>
        </ng-container>
      </div>
    </nz-card>
  </div>

  <h3 class="section-title">{{ 'INTEGRATION.OVERVIEW.SECTION.CONNECTED_PROVIDERS' | translate }}</h3>

  <nz-table [nzData]="connections" [nzLoading]="loading">
    <thead>
    <tr>
      <th>{{ 'INTEGRATION.OVERVIEW.TABLE.PROVIDER' | translate }}</th>
      <th>{{ 'INTEGRATION.OVERVIEW.TABLE.STATUS' | translate }}</th>
      <th>{{ 'INTEGRATION.OVERVIEW.TABLE.LAST_ACTIVITY' | translate }}</th>
      <th>{{ 'INTEGRATION.OVERVIEW.TABLE.MODULES' | translate }}</th>
      <th>{{ 'INTEGRATION.OVERVIEW.TABLE.SCOPE' | translate }}</th>
      <th>{{ 'INTEGRATION.OVERVIEW.TABLE.MODE' | translate }}</th>
      <th style="width: 240px;">{{ 'INTEGRATION.OVERVIEW.TABLE.ACTIONS' | translate }}</th>
    </tr>
    </thead>

    <tbody>
    <tr *ngFor="let c of connections">
      <td>{{ c.displayName || c.provider }}</td>
      <td><nz-tag [nzColor]="isSftpConnection(c) && c.sftpLifecycleStatus === 'active' ? 'green' : isSftpConnection(c) && c.sftpLifecycleStatus === 'error' ? 'red' : tagColor(c.status)">{{ isSftpConnection(c) ? sftpLifecycleLabel(c.sftpLifecycleStatus) : c.status }}</nz-tag></td>
      <td>
        <ng-container *ngIf="isSftpConnection(c); else genericLastActivity">
          {{ formatSftpDateTime(connectionActivityAt(c)) }}
        </ng-container>
        <ng-template #genericLastActivity>{{ connectionActivityAt(c) || '-' }}</ng-template>
      </td>
      <td>{{ enabledModulesCount(c) }}</td>
      <td>{{ scopeCount(c) }}</td>
      <td>{{ syncMode(c) }}</td>
      <td class="actions-col">
        <a (click)="goToStatus(c)">{{ 'INTEGRATION.OVERVIEW.STATUS' | translate }}</a>
        <ng-container *ngIf="!isSftpConnection(c)">
          <span class="sep">•</span>
          <a (click)="quickTest(c)">{{ 'INTEGRATION.STATUS.TEST' | translate }}</a>
          <span class="sep">•</span>
          <a (click)="quickSync(c)">{{ 'INTEGRATION.OVERVIEW.SYNC' | translate }}</a>
        </ng-container>
        <ng-container *ngIf="!isSftpConnection(c) || c.sftpLifecycleStatus !== 'disconnected'">
          <span class="sep">•</span>
          <a (click)="disconnect(c)">{{ 'INTEGRATION.STATUS.DISCONNECT' | translate }}</a>
        </ng-container>
      </td>
    </tr>

    <tr *ngIf="!loading && (!connections || connections.length === 0)">
      <td colspan="7">{{ 'INTEGRATION.OVERVIEW.NO_CONNECTED' | translate }}</td>
    </tr>
    </tbody>
  </nz-table>
</div>
